import React from 'react';
import Icon from '../../../components/AppIcon';

const RuleLibrary = ({
  rules,
  loading,
  filters,
  onFilterChange,
  onEdit,
  onDelete,
  onToggleStatus,
  onTest,
  onViewLogs
}) => {
  const triggerTypeLabels = {
    client_revenue_threshold: 'Client Revenue Threshold',
    service_frequency: 'Service Frequency',
    visit_count_milestone: 'Visit Count Milestone',
    membership_anniversary: 'Membership Anniversary',
    invoice_amount: 'Invoice Amount',
    payment_status: 'Payment Status',
    client_lifetime_value: 'Client Lifetime Value',
    service_interval: 'Service Interval',
    feedback_rating_threshold: 'Feedback Rating Threshold',
    service_issue_detection: 'Service Issue Detection'
  };

  const statusColors = {
    active: 'bg-success/10 text-success border-success/20',
    paused: 'bg-warning/10 text-warning border-warning/20',
    draft: 'bg-muted text-muted-foreground border-border',
    archived: 'bg-error/10 text-error border-error/20'
  };

  const triggerCategories = [
    { value: '', label: 'All Triggers' },
    { value: 'client_revenue_threshold', label: 'Client Thresholds', icon: 'DollarSign' },
    { value: 'visit_count_milestone', label: 'Service Milestones', icon: 'Award' },
    { value: 'invoice_amount', label: 'Revenue Events', icon: 'Receipt' },
    { value: 'feedback_rating_threshold', label: 'Feedback Ratings', icon: 'Star' },
    { value: 'service_issue_detection', label: 'Service Issues', icon: 'AlertTriangle' }
  ];

  return (
    <div>
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4">
          <div className="flex-1 min-w-0 sm:min-w-[180px]">
            <label className="block text-sm font-medium text-foreground mb-2">
              Trigger Category
            </label>
            <select
              value={filters?.triggerType || ''}
              onChange={(e) => onFilterChange({ ...filters, triggerType: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {triggerCategories?.map(cat => (
                <option key={cat?.value} value={cat?.value}>
                  {cat?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-0 sm:min-w-[180px]">
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={filters?.status || ''}
              onChange={(e) => onFilterChange({ ...filters, status: e?.target?.value })}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading automation rules...</p>
          </div>
        </div>
      ) : rules?.length === 0 ? (
        <div className="text-center py-16">
          <Icon name="Zap" size={64} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">
            No Automation Rules Yet
          </h3>
          <p className="text-muted-foreground mb-6">
            Create your first automation rule to trigger emails based on client behavior
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {rules?.map((rule) => (
            <div
              key={rule?.id}
              className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-lg transition-smooth w-full min-w-0"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4 gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-1 break-words">
                    {rule?.ruleName}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 break-words">
                    {rule?.description}
                  </p>
                </div>
                <button
                  onClick={() => onToggleStatus(rule?.id, !rule?.isActive)}
                  className={`ml-3 px-3 py-1 rounded-full text-xs font-medium transition-smooth ${
                    rule?.isActive
                      ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {rule?.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>

              {/* Trigger Type */}
              <div className="flex items-center gap-2 mb-4 p-3 bg-primary/5 rounded-lg">
                <Icon name="Zap" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {triggerTypeLabels?.[rule?.triggerType] || rule?.triggerType}
                </span>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 bg-background rounded-lg min-w-0">
                  <div className="text-base md:text-lg font-bold text-foreground truncate">
                    {rule?.executionCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Executions</div>
                </div>
                <div className="text-center p-2 bg-background rounded-lg min-w-0">
                  <div className="text-base md:text-lg font-bold text-success truncate">
                    {rule?.successCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Success</div>
                </div>
                <div className="text-center p-2 bg-background rounded-lg min-w-0">
                  <div className="text-base md:text-lg font-bold text-error truncate">
                    {rule?.failureCount || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                </div>
              </div>

              {/* Conditions Count */}
              <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                <Icon name="Filter" size={14} />
                <span>{rule?.conditions?.length || 0} condition(s)</span>
              </div>

              {/* Last Executed */}
              {rule?.lastExecutedAt && (
                <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>
                    Last run: {new Date(rule?.lastExecutedAt)?.toLocaleDateString()}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                <button
                  onClick={() => onEdit(rule)}
                  className="flex-1 min-w-[70px] flex items-center justify-center gap-1 px-2 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth text-sm"
                >
                  <Icon name="Edit" size={14} />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => onTest(rule)}
                  className="flex-1 min-w-[70px] flex items-center justify-center gap-1 px-2 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth text-sm"
                >
                  <Icon name="Play" size={14} />
                  <span>Test</span>
                </button>
                <button
                  onClick={() => onViewLogs(rule)}
                  className="px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
                  title="View Logs"
                >
                  <Icon name="History" size={14} />
                </button>
                <button
                  onClick={() => onDelete(rule?.id)}
                  className="px-3 py-2 bg-error/10 text-error rounded-lg hover:bg-error/20 transition-smooth"
                  title="Delete Rule"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RuleLibrary;